import { getEffectiveLockedFields } from './lockedFieldsClient';

import { normalizeLockKey } from '@/lib/brand/resolve-brand-kit-chain';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { EnhancedBaseService } from '@/services/base/EnhancedBaseService';
import type { BrandKit, BrandKitFormData } from '@/types/brand-kit';
import { logger } from '@/utils/logger';

/** Human-readable labels for locked-field error copy (block-not-inert). */
const LOCKED_FIELD_LABELS: Record<string, string> = {
  primaryColor: 'Primary color',
  secondaryColor: 'Secondary color',
  accentColor: 'Accent color',
  logoUrl: 'Logo',
  disclaimerText: 'Disclaimer',
  fontFamily: 'Font',
  brandGuidelines: 'Brand guidelines',
};

/** Maps BrandKitFormData/DB update keys to the canonical camelCase lock
 * vocabulary via normalizeLockKey (reused, never hand-rolled). */
const BRAND_KIT_KEY_TO_LOCK_KEY: Record<string, string> = {
  primary_color: 'primaryColor',
  primaryColor: 'primaryColor',
  secondary_color: 'secondaryColor',
  secondaryColor: 'secondaryColor',
  accent_color: 'accentColor',
  accentColor: 'accentColor',
  logo_url: 'logoUrl',
  logoUrl: 'logoUrl',
  disclaimer_text: 'disclaimerText',
  disclaimerText: 'disclaimerText',
  font_family: 'fontFamily',
  fontFamily: 'fontFamily',
  brand_guidelines: 'brandGuidelines',
  brandGuidelines: 'brandGuidelines',
};

const BRAND_KIT_PERMISSION_MESSAGE =
  'You do not have permission to change this brand. Ask an owner or Admin.';

export class LockedFieldEditError extends Error {
  lockedFields: string[];
  constructor(lockedFields: string[]) {
    const labels = lockedFields.map((f) => LOCKED_FIELD_LABELS[f] ?? f);
    super(
      `${labels.join(', ')} ${labels.length === 1 ? 'is' : 'are'} locked by your corporate brand kit — this ${labels.length === 1 ? 'field is' : 'fields are'} not editable.`,
    );
    this.name = 'LockedFieldEditError';
    this.lockedFields = lockedFields;
  }
}

class BrandKitService extends EnhancedBaseService {
  async getBrandKit(): Promise<BrandKit | null> {
    const { data, error } = await this.client.rpc('get_effective_brand_kit').maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data ?? null;
  }

  async getBrandKitById(kitId: string, userId?: string): Promise<BrandKit | null> {
    let query = this.client.from('brand_kits').select('*').eq('id', kitId);
    if (userId) {
      const ownerId = await resolveOwnerId(userId);
      query = query.eq('user_id', ownerId);
    }
    const { data, error } = await query.maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return data ?? null;
  }

  async getOrCreateBrandKit(workspaceName?: string): Promise<BrandKit> {
    const existing = await this.getBrandKit();
    if (existing) return existing;

    // Creation is server-owned. `ensure_default_workspace` resolves the caller's
    // owner and account, enforces owner-only creation, and serializes concurrent
    // first calls (Settings → Brand mounts two hooks that both land here), so
    // racing callers get the same kit instead of a 23505 on the one-default index.
    //
    // Never go back to `.from('brand_kits').insert(...).select()` here: RETURNING
    // re-checks the new row against `brand_kits_select_visible`, and before
    // 20261008024834 that rejected every new kit (403 42501) — the 2026-10-07
    // onboarding outage.
    const name = workspaceName?.trim();
    const { data, error } = await this.client
      .rpc('ensure_default_workspace', name ? { p_name: name } : {})
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Brand kit could not be created');
    return data as BrandKit;
  }

  async updateBrandKit(updates: Partial<BrandKitFormData>, kitId?: string): Promise<BrandKit> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // BI-5-03 pre-flight (Path 7 client-direct backstop, block-not-inert):
    // a franchise location editing an ancestor-locked field gets a blocking
    // error, not a silently-inert stored override (the resolver already
    // discards overrides at read time — this closes the write-time UX gap).
    // NEVER intersects against the display union `lockedFields` — a
    // corporate kit editing its own locked values must stay allowed
    // (phase-pinned semantic).
    await this.assertNoLockedKeysBlocked(updates);

    const existingKit = kitId
      ? await this.getBrandKitById(kitId)
      : await this.getOrCreateBrandKit();
    if (!existingKit) throw new Error('Brand kit not found');

    // Nothing to change is not an error. PostgREST treats an empty PATCH as a
    // no-op that returns zero rows, which `.single()` turns into PGRST116 (406)
    // — that failed onboarding for every Shopify install whose kit was already
    // named after the shop and had no scanned colours or logo. A key whose value
    // is `undefined` is dropped by JSON, so it counts as nothing too.
    if (Object.values(updates).every((value) => value === undefined)) return existingKit;

    const { data, error } = await this.client
      .from('brand_kits')
      .update(updates)
      .eq('id', existingKit.id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116' || error.code === '42501') {
        throw Object.assign(new Error(BRAND_KIT_PERMISSION_MESSAGE), { code: error.code });
      }
      throw error;
    }
    return data;
  }

  /** Throws LockedFieldEditError BEFORE any write when `updates` touches a
   * key in the INHERITED enforcement set (never the display union). */
  private async assertNoLockedKeysBlocked(updates: Record<string, unknown>): Promise<void> {
    const { inheritedLockedFields } = await getEffectiveLockedFields(this.client);
    if (inheritedLockedFields.length === 0) return;

    const inherited = new Set(inheritedLockedFields.map(normalizeLockKey));
    const blocked = Object.keys(updates)
      .map((key) => BRAND_KIT_KEY_TO_LOCK_KEY[key])
      .filter((lockKey): lockKey is string => !!lockKey && inherited.has(lockKey));

    if (blocked.length > 0) {
      throw new LockedFieldEditError(Array.from(new Set(blocked)));
    }
  }

  /** Throws LockedFieldEditError when logoUrl is in the INHERITED enforcement
   * set — shared by uploadLogo/deleteLogo for all variants (primary/light/dark). */
  private async assertLogoNotLocked(): Promise<void> {
    const { inheritedLockedFields } = await getEffectiveLockedFields(this.client);
    if (inheritedLockedFields.map(normalizeLockKey).includes('logoUrl')) {
      throw new LockedFieldEditError(['logoUrl']);
    }
  }

  async uploadLogo(
    file: File,
    variant: 'primary' | 'light' | 'dark',
    kitId?: string,
  ): Promise<void> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // BI-5-03: block BEFORE any storage I/O when logoUrl is inherited-locked
    // (covers primary/light/dark variants — all governed by the single
    // logoUrl lock key per the phase's interfaces).
    await this.assertLogoNotLocked();

    let fileToUpload = file;
    if (
      file.type.startsWith('image/') &&
      file.type !== 'image/svg+xml' &&
      file.type !== 'image/gif'
    ) {
      try {
        const { compressImage, getRecommendedQuality } =
          await import('@/services/media/imageCompression');
        const quality = getRecommendedQuality(file.size, false);
        const compressedFile = await compressImage(file, {
          quality,
          format: 'auto',
          useWebWorker: true,
        });
        fileToUpload = compressedFile;
      } catch (compressionError) {
        if (import.meta.env.DEV) {
          logger.warn(
            '[BrandKitService.uploadLogo] compression failed, using original',
            compressionError,
          );
        }
      }
    }

    const fileExt = (fileToUpload.name.split('.').pop() ?? 'png').toLowerCase();
    const columnName = variant === 'primary' ? 'logo_url' : `logo_${variant}_url`;

    // Delete the previous file if it was stored at a different path (e.g. the
    // user changed from .png → .svg). Different extension = different key.
    const existingKit = kitId
      ? await this.getBrandKitById(kitId)
      : await this.getOrCreateBrandKit();
    if (!existingKit) throw new Error('Brand kit not found');

    // Deterministic path per variant — no timestamp, so re-uploading the same
    // variant upserts in place rather than creating orphaned objects. Per-kit
    // path only when kitId is provided (multi-kit editing) — default-path
    // callers keep the legacy path so existing logo URLs stay valid.
    const fileName = kitId
      ? `${user.id}/${existingKit.id}/logo-${variant}.${fileExt}`
      : `${user.id}/logo-${variant}.${fileExt}`;
    const existingUrl = existingKit[columnName as keyof typeof existingKit] as string | null;
    let staleOldPath: string | null = null;
    if (existingUrl) {
      const marker = '/object/public/brand-assets/';
      const markerIdx = existingUrl.indexOf(marker);
      if (markerIdx !== -1) {
        const oldPath = existingUrl.slice(markerIdx + marker.length).split('?')[0];
        if (oldPath !== fileName) staleOldPath = oldPath;
      }
    }

    const { error: uploadError } = await this.client.storage
      .from('brand-assets')
      .upload(fileName, fileToUpload, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    const {
      data: { publicUrl },
    } = this.client.storage.from('brand-assets').getPublicUrl(fileName);

    // Cache-busting version param: the storage key stays deterministic
    // (upsert in place), but the stored URL changes on every upload so a
    // same-extension re-upload is never served from the 3600s storage
    // cache for up to an hour.
    const versionedUrl = `${publicUrl}?v=${Date.now()}`;

    const { error: updateError } = await this.client
      .from('brand_kits')
      .update({ [columnName]: versionedUrl })
      .eq('id', existingKit.id)
      .select('id')
      .single();

    if (updateError) {
      if (!existingUrl?.includes(`/object/public/brand-assets/${fileName}`)) {
        await this.client.storage.from('brand-assets').remove([fileName]);
      }
      if (updateError.code === 'PGRST116' || updateError.code === '42501') {
        throw Object.assign(new Error(BRAND_KIT_PERMISSION_MESSAGE), { code: updateError.code });
      }
      throw updateError;
    }

    if (staleOldPath) {
      await this.client.storage.from('brand-assets').remove([staleOldPath]);
    }
  }

  async deleteLogo(variant: 'primary' | 'light' | 'dark', kitId?: string): Promise<void> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // BI-5-03: block before any storage I/O when logoUrl is inherited-locked.
    await this.assertLogoNotLocked();

    const columnName = variant === 'primary' ? 'logo_url' : `logo_${variant}_url`;

    const existingKit = kitId
      ? await this.getBrandKitById(kitId)
      : await this.getOrCreateBrandKit();
    if (!existingKit) throw new Error('Brand kit not found');

    const { error } = await this.client
      .from('brand_kits')
      .update({ [columnName]: null })
      .eq('id', existingKit.id);

    if (error) throw error;
  }
}

export const brandKitService = new BrandKitService();
export { BrandKitService };
